import classNames from "classnames";
import moment from "moment";
import React from "react";
import { actions, Control } from "react-redux-form";
import { Dispatch } from "redux";
import _ from "underscore";

import { DateRangePicker, FocusedInputShape } from "react-dates";
import * as DateUtil from "@travellocal/utils/dateHelpers";
import { Errors, RadioButton, NumberStepper } from "../../../../core/atoms/rrf";
import { neverHere } from "@travellocal/utils/tsHelpers";
import { CommonProps, EnquiryFormSection } from "./common";
import { FormProgressIcon } from "./formProgressIcon";

import "./travelDate.scss";

interface OwnProps {
  dispatch: Dispatch<IWebStoreState>;
}

interface State {
  startDate: moment.Moment;
  endDate: moment.Moment;
  monthsToShow: number;
  focusedInput: FocusedInputShape;
}

export class TravelDate extends EnquiryFormSection<OwnProps, Partial<State>> {

  public constructor(props) {
    super(props);
    this.state = {
      startDate: null,
      endDate: null,
      monthsToShow: 2,
      focusedInput: "startDate",
    };
  }

  protected shouldUpdateValidity(oldProps: CommonProps, newProps: CommonProps) {
    return !_.isEqual(oldProps.formState.travelDate, newProps.formState.travelDate);
  }

  protected syncValidity(formState: Forms.EnquiryForm) {
    if (formState.travelDate == null) {
      return;
    }

    let fieldErrors: { [key: string]: boolean };
    if (formState.travelDate.travelDateFlexibility == null) {
      fieldErrors = {
        required: true,
      };
    }
    else {
      fieldErrors = {
        exactDateRequired: false,
        monthRequired: false,
        lengthRequired: formState.travelDate.preferredTravelNumberOfDays == null || formState.travelDate.preferredTravelNumberOfDays < 1,
      };

      const monthAndYear = formState.travelDate.preferredTravelMonth;
      switch (formState.travelDate.travelDateFlexibility) {
        case TravelDateFlexibility.ExactDates:
          fieldErrors.exactDateRequired = formState.travelDate.preferredTravelExactDate == null;
          break;
        case TravelDateFlexibility.RoughDates:
          fieldErrors.monthRequired = !monthAndYear || !monthAndYear.month || !monthAndYear.year;
          break;
        case TravelDateFlexibility.Unknown:
          break;
        default:
          neverHere(formState.travelDate.travelDateFlexibility);
          break;
      }
    }

    this.props.dispatch(actions.setErrors(this.props.model, fieldErrors));
  }

  private selectExactDates = () => this.props.dispatch(actions.change(`${this.props.model}.travelDateFlexibility`, TravelDateFlexibility.ExactDates));

  private selectRoughDates = () => this.props.dispatch(actions.change(`${this.props.model}.travelDateFlexibility`, TravelDateFlexibility.RoughDates));

  private selectUnknownDates = () => this.props.dispatch(actions.change(`${this.props.model}.travelDateFlexibility`, TravelDateFlexibility.Unknown));

  private renderLengthSpinner = () => {
    const model = `${this.props.model}.preferredTravelNumberOfDays`;
    return (
      <div className="travel-date__stepper">
        <label htmlFor={model}>Number of days</label>
        <NumberStepper
          title="Number of days"
          model={model}
          min={0}
          max={365}
        />
      </div>
    );
  }

  private renderExactDatesForm = (travelDate: Forms.EnquiryFormTravelDate) => {

    const handleDateChange = async (startDate: moment.Moment, endDate: moment.Moment) => {
      // for timezones like Samoa's (+13GMT) airbnbs react-dates returns null on first load, this catches it
      if (startDate != null) {
        const newStartDate = DateUtil.momentToDate(startDate);
        await this.props.dispatch(actions.change(`${this.props.model}.preferredTravelExactDate`, newStartDate));

        if (endDate != null) {
          const newEndDate = DateUtil.momentToDate(endDate);
          const tripLength = newStartDate ? Math.abs(DateUtil.diffDays(newEndDate, newStartDate)) : 0;
          await this.props.dispatch(actions.change(`${this.props.model}.preferredTravelNumberOfDays`, tripLength));
          this.setState({ startDate, endDate });
        }
        else {
          this.setState({ startDate, endDate });
        }
      }
    };

    const locationName = this.props.formState.location
      ? this.props.formState.location.name
      : "your chosen location";

    return (
      <div className="exact-dates-section additional-info-section">
        <header>
          <h3>I have exact dates</h3>
          <p>Tell us the dates of your arrival into and departure from {locationName}.
           If you&#39;ve already booked flights, let us know in the comments field at the end of the form.</p>
          <p><small>Choose <a onClick={this.selectRoughDates}>rough dates</a> or <a onClick={this.selectUnknownDates}>ask for advice</a> instead...</small></p>
          <hr />
        </header>

        <Errors model={this.props.model} messages={{ exactDateRequired: "Please tell us your arrival and departure dates" }} />

        <div className="picker">

          <DateRangePicker
            startDate={this.state.startDate}
            startDateId="enquiry_arrival_date"
            endDate={this.state.endDate}
            endDateId="enquiry_departure_date"
            onDatesChange={({ startDate, endDate }) => handleDateChange(startDate, endDate)}
            focusedInput={this.state.focusedInput}
            onFocusChange={focusedInput => this.setState({ focusedInput })}
            startDatePlaceholderText={"Arrival"}
            endDatePlaceholderText={"Departure"}
            noBorder={true}
            numberOfMonths={this.state.monthsToShow}
          />
        </div>

        {(travelDate.preferredTravelNumberOfDays > 0) &&
          <p>Trip length: <strong>{travelDate.preferredTravelNumberOfDays}</strong> days</p>
        }
      </div>
    );
  }

  private renderRoughDatesForm() {

    const getSelectValue = (e) => {
      // e can either be an Event or a value
      const isEvent = !!(e?.stopPropagation && e?.preventDefault);
      return isEvent ? parseInt((e.target as HTMLSelectElement).value) : parseInt((e));
    };

    return (
      <div className="rough-dates-section additional-info-section">
        <header>
          <h3>I have a rough idea</h3>
          <p>Let us know the month and year you&#39;re planning your trip for.</p>
          <p><small>Choose <a onClick={this.selectExactDates}>exact dates</a> or <a onClick={this.selectUnknownDates}>ask for advice</a> instead...</small></p>
          <hr />
        </header>

        <Errors model={this.props.model} messages={{ monthRequired: "Please choose a month and a year for your trip to start in" }} />

        <p>I want to travel in...</p>
        <div className="month-year-wrap">
          <Control.select model={`${this.props.model}.preferredTravelMonth.month`} id="travel-date-preferred-month" getValue={getSelectValue}>
            <option>Month...</option>
            {_.range(0, 12).map(i => (
              <option key={i} value={i + 1}>{moment().month(i).format("MMMM")}</option>),
            )}
          </Control.select>

          <Control.select model={`${this.props.model}.preferredTravelMonth.year`} id="travel-date-preferred-year" getValue={getSelectValue}>
            <option>Year...</option>
            {_.range(0, 4).map(i => {
              const year = moment().add(i, "year").year();
              return <option key={year} value={year}>{year}</option>;
            })}
          </Control.select>
        </div>

        <Errors model={this.props.model} messages={{ lengthRequired: "Please choose how long you want your trip to be" }} />

        {this.renderLengthSpinner()}
      </div>
    );
  }

  private renderUnknownDatesForm() {
    return (
      <div className="unknown-dates-section additional-info-section">
        <header>
          <h3>I haven&#39;t decided</h3>
          <p>That&#39;s ok! Just let us know roughly how long you want your trip to be. Our local experts will advise you on the best time to visit your destination.</p>
          <p><small>Choose <a onClick={this.selectExactDates}>exact dates</a> or <a onClick={this.selectRoughDates}>rough dates</a> instead...</small></p>
          <hr />
        </header>

        <Errors model={this.props.model} messages={{ lengthRequired: "Please choose how long you want your trip to be" }} />

        {this.renderLengthSpinner()}
      </div>
    );
  }

  private renderAdditionalInfoForm = (travelDate: Forms.EnquiryFormTravelDate) => {
    switch (travelDate.travelDateFlexibility) {
      case TravelDateFlexibility.ExactDates:
        return this.renderExactDatesForm(travelDate);
      case TravelDateFlexibility.RoughDates:
        return this.renderRoughDatesForm();
      case TravelDateFlexibility.Unknown:
        return this.renderUnknownDatesForm();
      default:
        neverHere(travelDate.travelDateFlexibility);
    }
  }

  private handleResize = () => {
    if (window.innerWidth <= 630) {
      this.setState({ monthsToShow: 1 });
    }
    else {
      this.setState({ monthsToShow: 2 });
    }
  }

  public componentDidMount() {
    this.handleResize();
    requestAnimationFrame(() => {
      window.addEventListener("resize", this.handleResize);
    });
  }

  public componentWillUnmount() {
    window.removeEventListener("resize", this.handleResize);
  }

  public render() {
    const { model, className, id, formState } = this.props;
    const travelDate = formState?.travelDate ?? {} as Forms.EnquiryFormTravelDate;

    return (
      <section id={id} className={classNames("travel-date", className)}>
        <header>
          <FormProgressIcon model={model} />
          <h2>When would you like to go?</h2>
        </header>

        <div className="enquiry-form-question">
          <Errors model={model} messages={{ required: "Please select an option" }} />

          {!travelDate.travelDateFlexibility &&
            <div className="initial-info-section">
              <RadioButton
                model={`${model}.travelDateFlexibility`}
                id={`travel-date-${TravelDateFlexibility.ExactDates}`}
                name="travel-date"
                value={TravelDateFlexibility.ExactDates}
                label="I have exact dates"
                subLabel="Choose a start date and end date for your trip"
                getValue={e => parseInt((e as React.ChangeEvent<HTMLInputElement>).target.value)}
              />

              <RadioButton
                model={`${model}.travelDateFlexibility`}
                id={`travel-date-${TravelDateFlexibility.RoughDates}`}
                name="travel-date"
                value={TravelDateFlexibility.RoughDates}
                label="I have a rough idea"
                subLabel="Choose a month and year"
                getValue={e => parseInt((e as React.ChangeEvent<HTMLInputElement>).target.value)}
              />

              <RadioButton
                model={`${model}.travelDateFlexibility`}
                id={`travel-date-${TravelDateFlexibility.Unknown}`}
                name="travel-date"
                value={TravelDateFlexibility.Unknown}
                label="I haven't decided"
                subLabel="Our local experts can help you choose"
                getValue={e => parseInt((e as React.ChangeEvent<HTMLInputElement>).target.value)}
              />
            </div>
          }

          {travelDate.travelDateFlexibility && this.renderAdditionalInfoForm(travelDate)}
        </div>
      </section>
    );
  }
}
